/*
 * MommyHana floating add-to-cart bar (SPEC-PHASE05 §5).
 *
 * Paired with snippets/mh-floating-bar.liquid. No dependencies, no external request.
 *
 * Two behaviours, chosen per instance by `data-mh-always` on the bar:
 *
 *   - **Always visible.** The product page pins the bar from first paint and keeps it there
 *     (SPEC-PHASE1 follow-up, item 1). Nothing is observed; the bar is simply shown.
 *   - **On scroll past** (the Phase 05 default, still used by the landing buy card, below).
 *
 * The scroll behaviour: the bar shows only once the main Add to Cart button has left the viewport **upward**, and
 * hides again when it comes back. `isIntersecting` alone is not enough for that: it is equally
 * false when the button is still below the fold, which on a long product page is most of the
 * time the customer spends before reaching it. Offering "Add to Cart" in a fixed bar while the
 * real button is still ahead of them is what the "upward" half of the rule prevents, so the
 * sign of `boundingClientRect.top` is checked as well.
 *
 * The bar carries no state and no fallback timer. If IntersectionObserver is unavailable the bar
 * simply never appears, and the page keeps its own Add to Cart button, which is the thing that
 * actually sells.
 */
(function () {
  'use strict';

  if (window.mhFloatingBarLoaded) return;
  window.mhFloatingBarLoaded = true;

  function start() {
    var bars = document.querySelectorAll('[data-mh-floating-bar]');
    if (!bars.length) return;

    Array.prototype.forEach.call(bars, function (bar) {
      // Pinned from load. No observer, so this also works where IntersectionObserver does not.
      if (bar.hasAttribute('data-mh-always')) {
        bar.classList.add('is-visible');

        // CI-7: pinned, but it stands down while a named element is on screen. The product page
        // uses this to stop the bar covering the product title on a short viewport. Without
        // `data-mh-hide-when`, or without IntersectionObserver, the bar simply stays pinned, which
        // is exactly the behaviour this branch had before.
        var pinnedHide = bar.dataset.mhHideWhen
          ? document.getElementById(bar.dataset.mhHideWhen)
          : null;
        if (pinnedHide && 'IntersectionObserver' in window) {
          new IntersectionObserver(
            function (entries) {
              bar.classList.toggle('is-visible', !entries[entries.length - 1].isIntersecting);
            },
            { threshold: 0 }
          ).observe(pinnedHide);
        }
        return;
      }

      if (!('IntersectionObserver' in window)) return;

      // Landing mode (CHANGE-SPEC-first-book-neelofa change item 2), opted into by
      // `data-mh-show-after`. Reached only by a bar that asks for it, so the two modes above are
      // untouched.
      //
      // Two independent facts decide one class. The Phase 05 rule watches the buy button and is
      // right for a product page, where that button is most of the way down; this page has to
      // start offering the bar as soon as the HERO is gone, which is a different element and a
      // different moment. And it has to stop offering it while the real buy section is on screen,
      // or the customer would be looking at two Add to Cart buttons at once.
      if (bar.dataset.mhShowAfter) {
        var after = document.getElementById(bar.dataset.mhShowAfter);
        if (!after) return;

        var hideEl = bar.dataset.mhHideWhen ? document.getElementById(bar.dataset.mhHideWhen) : null;
        var heroGone = false;
        var buyInView = false;

        var sync = function () {
          bar.classList.toggle('is-visible', heroGone && !buyInView);
        };

        // Same "upward" test as the Phase 05 rule: `isIntersecting` is equally false before the
        // hero has been reached, which on first paint is never true here but would be after an
        // in-page anchor jump backwards.
        new IntersectionObserver(
          function (entries) {
            var entry = entries[entries.length - 1];
            heroGone = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            sync();
          },
          { threshold: 0 }
        ).observe(after);

        if (hideEl) {
          new IntersectionObserver(
            function (entries) {
              buyInView = entries[entries.length - 1].isIntersecting;
              sync();
            },
            { threshold: 0 }
          ).observe(hideEl);
        }

        return;
      }

      var form = document.getElementById(bar.dataset.mhAnchor);
      if (!form) return;

      // The button inside the form, not the bar's own button — which carries the same
      // data attributes but is associated with the form by its `form=` attribute rather than
      // by containment, and so is never a descendant of it.
      var anchor = form.querySelector('[data-mh-atc]');
      if (!anchor) return;

      new IntersectionObserver(
        function (entries) {
          var entry = entries[entries.length - 1];
          var gone = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          bar.classList.toggle('is-visible', gone);
        },
        { threshold: 0 }
      ).observe(anchor);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
